// React Query hooks for the skip job-types + stages + actions surface.
//
// Mirrors useSkipStock — same forSelection pattern, same activate/
// deactivate semantics, plus stages and actions CRUD on top.
//
// Server endpoints live under /api/v1/skip/job-types — see
// server/src/modules/skip/jobTypes/router.ts for the full contract.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  CreateSkipJobTypeActionInput,
  CreateSkipJobTypeInput,
  CreateSkipJobTypeStageInput,
  SkipJobType,
  SkipJobTypeAction,
  SkipJobTypeStage,
  SkipJobTypeWithChildren,
  UpdateSkipJobTypeActionInput,
  UpdateSkipJobTypeInput,
  UpdateSkipJobTypeStageInput,
} from '../../../types/api';

// ============================================================ TYPES

/**
 * List job types for the calling tenant.
 *
 * Default mode (no options) returns every global alongside own-tenant
 * types — the SkipJobTypesPage uses this so it can still render a
 * toggle for non-activated globals and show is_child_only globals as
 * read-only "System" rows.
 *
 * Pass `{ forSelection: true }` for job/enquiry create dropdowns. The
 * server filters is_child_only=0, is_active=1, and non-activated
 * globals — so the dropdown shows only types the user can actually
 * pick. Cache key carries the flag so the two modes don't share
 * entries.
 */
export function useSkipJobTypes(options: { forSelection?: boolean } = {}) {
  const forSelection = options.forSelection ?? false;
  return useQuery<{ jobTypes: SkipJobType[] }>({
    queryKey: queryKeys.skipJobTypes.list({ forSelection }),
    queryFn: async () => {
      const res = await apiClient.get<{ jobTypes: SkipJobType[] }>(
        endpoints.skipJobTypes.list,
        forSelection ? { params: { for_selection: 'true' } } : undefined,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useSkipJobTypeDetail(encId: string | null | undefined) {
  return useQuery<{ jobType: SkipJobTypeWithChildren }>({
    queryKey: queryKeys.skipJobTypes.detail(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipJobTypeDetail called without enc_id');
      const res = await apiClient.get<{ jobType: SkipJobTypeWithChildren }>(
        endpoints.skipJobTypes.type(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

export function useCreateSkipJobType() {
  const qc = useQueryClient();
  return useMutation<
    { jobType: SkipJobTypeWithChildren },
    Error,
    CreateSkipJobTypeInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { jobType: SkipJobTypeWithChildren },
        CreateSkipJobTypeInput
      >(endpoints.skipJobTypes.list, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

export function useUpdateSkipJobType(encId: string) {
  const qc = useQueryClient();
  return useMutation<
    { jobType: SkipJobTypeWithChildren },
    Error,
    UpdateSkipJobTypeInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { jobType: SkipJobTypeWithChildren },
        UpdateSkipJobTypeInput
      >(endpoints.skipJobTypes.type(encId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(encId),
      });
    },
  });
}

export function useDeleteSkipJobType(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: boolean }>(
        endpoints.skipJobTypes.type(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

// ============================================================ ACTIVATIONS

/**
 * Activate or deactivate a global default job type for the calling
 * tenant. Both calls are idempotent server-side. The settings page
 * does its own optimistic update; this hook only owns the network
 * round-trip and cache invalidation.
 */
export function useActivateSkipJobType() {
  const qc = useQueryClient();
  return useMutation<{ jobType: SkipJobType }, Error, { encId: string }>({
    mutationFn: async ({ encId }) => {
      const res = await apiClient.post<{ jobType: SkipJobType }, undefined>(
        endpoints.skipJobTypes.activateGlobal(encId),
        undefined,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

export function useDeactivateSkipJobType() {
  const qc = useQueryClient();
  return useMutation<{ jobType: SkipJobType }, Error, { encId: string }>({
    mutationFn: async ({ encId }) => {
      const res = await apiClient.delete<{ jobType: SkipJobType }>(
        endpoints.skipJobTypes.activateGlobal(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

// ============================================================ STAGES

export function useSkipJobTypeStages(typeEncId: string | null | undefined) {
  return useQuery<{ stages: SkipJobTypeStage[] }>({
    queryKey: queryKeys.skipJobTypes.stages(typeEncId ?? ''),
    queryFn: async () => {
      if (!typeEncId) throw new Error('useSkipJobTypeStages called without typeEncId');
      const res = await apiClient.get<{ stages: SkipJobTypeStage[] }>(
        endpoints.skipJobTypes.stages(typeEncId),
      );
      return res.data;
    },
    enabled: !!typeEncId,
    staleTime: 30_000,
  });
}

export function useCreateSkipJobTypeStage(typeEncId: string) {
  const qc = useQueryClient();
  return useMutation<
    { stage: SkipJobTypeStage },
    Error,
    CreateSkipJobTypeStageInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { stage: SkipJobTypeStage },
        CreateSkipJobTypeStageInput
      >(endpoints.skipJobTypes.stages(typeEncId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.stages(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

export function useUpdateSkipJobTypeStage(
  typeEncId: string,
  stageEncId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    { stage: SkipJobTypeStage },
    Error,
    UpdateSkipJobTypeStageInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { stage: SkipJobTypeStage },
        UpdateSkipJobTypeStageInput
      >(endpoints.skipJobTypes.stage(typeEncId, stageEncId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.stages(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

export function useDeleteSkipJobTypeStage(
  typeEncId: string,
  stageEncId: string,
) {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: boolean }>(
        endpoints.skipJobTypes.stage(typeEncId, stageEncId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.stages(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

// ============================================================ ACTIONS

export function useSkipJobTypeActions(typeEncId: string | null | undefined) {
  return useQuery<{ actions: SkipJobTypeAction[] }>({
    queryKey: queryKeys.skipJobTypes.actions(typeEncId ?? ''),
    queryFn: async () => {
      if (!typeEncId) throw new Error('useSkipJobTypeActions called without typeEncId');
      const res = await apiClient.get<{ actions: SkipJobTypeAction[] }>(
        endpoints.skipJobTypes.actions(typeEncId),
      );
      return res.data;
    },
    enabled: !!typeEncId,
    staleTime: 30_000,
  });
}

export function useCreateSkipJobTypeAction(typeEncId: string) {
  const qc = useQueryClient();
  return useMutation<
    { action: SkipJobTypeAction },
    Error,
    CreateSkipJobTypeActionInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.post<
        { action: SkipJobTypeAction },
        CreateSkipJobTypeActionInput
      >(endpoints.skipJobTypes.actions(typeEncId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.actions(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

export function useUpdateSkipJobTypeAction(
  typeEncId: string,
  actionEncId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    { action: SkipJobTypeAction },
    Error,
    UpdateSkipJobTypeActionInput
  >({
    mutationFn: async (body) => {
      const res = await apiClient.patch<
        { action: SkipJobTypeAction },
        UpdateSkipJobTypeActionInput
      >(endpoints.skipJobTypes.action(typeEncId, actionEncId), body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.actions(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}

export function useDeleteSkipJobTypeAction(
  typeEncId: string,
  actionEncId: string,
) {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: boolean }>(
        endpoints.skipJobTypes.action(typeEncId, actionEncId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.actions(typeEncId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobTypes.detail(typeEncId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobTypes.all });
    },
  });
}
